import { RootState } from '@reducers/index';
import { getChallengeGroup } from '@selectors/challengeGroups';
import { getChallenge } from '@selectors/challenges';
import dayjs from 'dayjs';
import ActionButton from 'lib/src/components/button/ActionButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import LinkButton from '@components/button/LinkButton';
import DataCheck from 'lib/src/components/common/DataCheck';
import Table from 'lib/src/components/table/Table';
import { Column } from 'lib/src/types/table';
import { isEmpty } from 'lib/src/utils/generic';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Submission } from 'src/types/shared/Assessments';
import useHasChallengeCrudPermissions from '../../../utils/hooks/useHasChallengeCrudPermissions';
import { downloadCSV } from '../../../utils/generic';
import DeleteSubmissionModal from './DeleteSubmissionModal';

interface Props {
    id: number;
    submissions: Submission[];
    isFetching: boolean;
    fetchError: string | null;
}

const ChallengeSubmissionsTable = ({ id, submissions, isFetching, fetchError }: Props) => {
    const [submissionToDelete, setSubmissionToDelete] = useState<number | null>(null);

    const getPercentComplete = ({ percentComplete }: Submission) => {
        return Math.round(percentComplete * 100);
    };

    const sortSubmissions = (submissions: Submission[]): Submission[] => {
        const sortedByPercent = submissions.reduce(
            (acc: Record<string, Submission[]>, curr: Submission): Record<string, Submission[]> => {
                const thisPercent = getPercentComplete(curr);
                if (acc.hasOwnProperty(thisPercent)) {
                    acc[thisPercent].push(curr);
                } else {
                    acc[thisPercent] = [curr];
                }
                return acc;
            },
            {},
        );
        return Object.keys(sortedByPercent)
            .sort((a: string, b: string) => +b - +a)
            .map(key =>
                sortedByPercent[key].sort((a, b) => {
                    if (!b.submittedOn || !a.submittedOn) return 0;
                    return dayjs(b.submittedOn).valueOf() - dayjs(a.submittedOn).valueOf();
                }),
            )
            .flat();
    };

    const challenge = useSelector((state: RootState) => getChallenge(state, id));
    const challengeGroup = useSelector((state: RootState) =>
        getChallengeGroup(state, challenge?.challengeGroupID),
    );

    const hasChallengeCrudPermissions = useHasChallengeCrudPermissions(challengeGroup, challenge);

    const columns: Column<Submission>[] = [
        {
            key: 1,
            heading: 'Applicant',
            getValue: (row: Submission) =>
                `${row.companyName ?? ''} ${row.email ? `(${row.email})` : ''}`,
            searchable: true,
            getSort: (a: Submission, b: Submission) =>
                getPercentComplete(b) - getPercentComplete(a),
        },
        {
            key: 2,
            heading: 'Created on',
            getValue: (row: Submission) =>
                row.createdOn ? dayjs(row.createdOn).format('DD/MM/YYYY') : '',
        },
        {
            key: 3,
            heading: 'Percent complete',
            getValue: (row: Submission) => getPercentComplete(row) + '%',
        },
        {
            key: 4,
            heading: 'Completed on',
            getValue: (row: Submission) =>
                row.submittedOn ? dayjs(row.submittedOn).format('DD/MM/YYYY') : '',
        },
        {
            key: 5,
            heading: '',
            getValue: (row: Submission) => (
                <ButtonRow>
                    <LinkButton
                        href={`/assessments/submissions/submission/${row.id}`}
                        color="green"
                        icon="search"
                    >
                        View
                    </LinkButton>

                    {hasChallengeCrudPermissions && (
                        <ActionButton
                            onClick={() => setSubmissionToDelete(row.id)}
                            color="red"
                            icon="times"
                        >
                            Remove
                        </ActionButton>
                    )}
                </ButtonRow>
            ),
        },
    ];

    return (
        <DataCheck
            dataExists={!isEmpty(submissions)}
            isFetching={isFetching}
            error={fetchError}
            errorMessage="There have been no submissions submitted"
        >
            <Table columns={columns} rows={sortSubmissions(submissions)} isLoading={false} />
            <ActionButton
                onClick={() =>
                    downloadCSV(`admin/challenges/${id}/export`, 'SubmissionsSummary.csv')
                }
            >
                Download as CSV
            </ActionButton>

            {!!submissionToDelete && (
                <DeleteSubmissionModal
                    id={submissionToDelete}
                    setSubmissionToDelete={setSubmissionToDelete}
                />
            )}
        </DataCheck>
    );
};

export default ChallengeSubmissionsTable;
